import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  arrayRemove,
  arrayUnion,
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { FaHeart, FaRegHeart } from "react-icons/fa";
import moment from "moment";
import ReactPlayer from "react-player";
import { db } from "../../lib/firebase";
import MainButton from "../../components/Button";
import SpinnerIcon from "../../assets/svgs/spinner";
import { ArrowLeft } from "lucide-react";
import { EventData } from "../../interface/event.interface";
import { UserData } from "../../interface/user.interface";
import { useUserRole } from "../../hooks/useUserRole.hook";
import { storageConst } from "../../utils/constants";
import LocationMap from "./components/locationMap";
import { Image } from "antd";

const EventDetailComponent: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [eventData, setEventData] = useState<EventData | null>(null);
  const [participants, setParticipants] = useState<UserData[]>([]);
  const [author, setAuthor] = useState<UserData | null>(null);
  const [isFollower, setIsFollower] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isFav, setIsFav] = useState(false);
  const [isJoin, setIsJoin] = useState(false);
  const [loading, setLoading] = useState(false);
  const loginuserRole = useUserRole(storageConst.APPTOKEN);
  const userId = localStorage.getItem(storageConst.APPTOKEN); // Replace with your user ID logic

  const handleFollow = async () => {
    if (!userId) {
      alert("Please log in to follow the author.");
      return;
    }
    if (!author) {
      return;
    }
    const authorRef = doc(db, "users", author.id);
    const userRef = doc(db, "users", userId);

    try {
      setIsFollower(!isFollower);
      if (isFollower) {
        await updateDoc(authorRef, {
          followers: arrayRemove(userId),
        });
        await updateDoc(userRef, {
          following: arrayRemove(author.id),
        });
      } else {
        await updateDoc(authorRef, {
          followers: arrayUnion(userId),
        });
        await updateDoc(userRef, {
          following: arrayUnion(author.id),
        });
      }
    } catch (error) {
      console.error("Error updating follow status:", error);
    }
  };

  useEffect(() => {
    const fetchEventData = async () => {
      if (!id) return;
      try {
        const eventDoc = doc(db, "products", id as string);
        const eventSnapshot = await getDoc(eventDoc);
        if (eventSnapshot.exists()) {
          const eventData = eventSnapshot.data() as EventData;
          setEventData(eventData);

          // Check join and favorite status
          if ((eventData.join || []).includes(userId!)) setIsJoin(true);
          if ((eventData.favrioute || []).includes(userId!)) setIsFav(true);

          // Fetch author details
          const authorDoc = doc(db, "users", eventData.uid);
          const authorSnapshot = await getDoc(authorDoc);
          if (authorSnapshot.exists()) {
            setAuthor({
              id: authorSnapshot.id,
              ...authorSnapshot.data(),
            } as UserData);
          }

          // Check follow status
          const followQuery = query(
            collection(db, "users"),
            where("uid", "==", userId),
            where("following", "array-contains", eventData.uid)
          );
          const followSnapshot = await getDocs(followQuery);
          if (!followSnapshot.empty) setIsFollower(true);
        } else {
          console.error("No such event document!");
        }
      } catch (error) {
        console.error("Error fetching event data:", error);
      }
    };

    fetchEventData();
  }, [id, userId]);
  useEffect(() => {
    const fetchParticipants = async () => {
      if (!eventData || eventData.join.length === 0) return;

      try {
        const usersRef = collection(db, "users");
        const usersPromises = eventData.join.map(async (userId) => {
          const userDoc = doc(usersRef, userId);
          const userSnapshot = await getDoc(userDoc);
          return userSnapshot.exists()
            ? { id: userId, ...userSnapshot.data() }
            : null;
        });

        const users = await Promise.all(usersPromises);
        setParticipants(users.filter((user) => user !== null) as UserData[]); // Filter out null values
      } catch (error) {
        console.error("Error fetching participants:", error);
      }
    };

    fetchParticipants();
  }, [eventData]);
  const handleFavorite = async () => {
    if (!userId) return alert("Please log in to favorite this event.");

    try {
      const eventRef = doc(db, "products", id!);
      setIsFav(!isFav);

      await updateDoc(eventRef, {
        favrioute: isFav ? arrayRemove(userId) : arrayUnion(userId),
      });
    } catch (error) {
      console.error("Error updating favorite status:", error);
    }
  };

  // const handleJoin = async () => {
  //   if (!userId) return alert("Please log in to join this event.");

  //   try {
  //     const eventRef = doc(db, "products", id!);
  //     setIsJoin(!isJoin);

  //     await updateDoc(eventRef, {
  //       join: isJoin ? arrayRemove(userId) : arrayUnion(userId),
  //     });
  //   } catch (error) {
  //     console.error("Error updating join status:", error);
  //   }
  // };
  const handleJoin = async () => {
    if (!userId) return alert("Please log in to join or leave this event.");

    try {
      const eventRef = doc(db, "products", id!);
      const newJoinStatus = !isJoin; // Toggle join status
      setIsJoin(newJoinStatus);

      // Update Firestore
      await updateDoc(eventRef, {
        join: newJoinStatus ? arrayUnion(userId) : arrayRemove(userId),
      });

      // Update local state
      setEventData((prevData) =>
        prevData
          ? {
              ...prevData,
              join: newJoinStatus
                ? [...prevData.join, userId] // Add user to join array
                : prevData.join.filter((uid) => uid !== userId), // Remove user from join array
            }
          : null
      );

      // Update participants list locally
      if (newJoinStatus) {
        // Add user to participants
        const userDoc = doc(db, "users", userId);
        const userSnapshot = await getDoc(userDoc);
        if (userSnapshot.exists()) {
          const newUser = { id: userId, ...userSnapshot.data() } as UserData;
          setParticipants((prev) => [...prev, newUser]);
        }
      } else {
        // Remove user from participants
        setParticipants((prev) => prev.filter((user) => user.id !== userId));
      }
    } catch (error) {
      console.error("Error updating join status:", error);
    }
  };

  const fetchParticipants = async () => {
    if (!eventData || eventData.join.length === 0) return;

    try {
      const usersRef = collection(db, "users");
      const usersPromises = eventData.join.map(async (userId) => {
        const userDoc = doc(usersRef, userId);
        const userSnapshot = await getDoc(userDoc);
        return userSnapshot.exists()
          ? { id: userId, ...userSnapshot.data() }
          : null;
      });

      const users = await Promise.all(usersPromises);
      setParticipants(users.filter((user) => user !== null) as UserData[]); // Filter out null values
    } catch (error) {
      console.error("Error fetching participants:", error);
    }
  };

  useEffect(() => {
    fetchParticipants();
  }, [eventData?.join]);
  const handlePublishToggle = async () => {
    if (!eventData) return;
    setLoading(true);
    try {
      const eventRef = doc(db, "products", id!);
      await updateDoc(eventRef, {
        publish: !eventData.publish,
      });
      setEventData((prevData) => ({
        ...prevData!,
        publish: !prevData!.publish,
      }));
    } catch (error) {
      console.error("Error updating publish status:", error);
    } finally {
      setLoading(false);
    }
  };
  const deletePost = async () => {
    if (!window.confirm("Are you sure you want to delete this event?")) {
      return;
    }
    setDeleteLoading(true);
    try {
      const eventRef = doc(db, "products", id!);
      await deleteDoc(eventRef);
      navigate(-1);
    } catch (error) {
      console.error("Error updating publish status:", error);
    } finally {
      setDeleteLoading(false);
    }
  };
  if (!eventData) return <div>Loading...</div>;
  return (
    <div className="min-h-screen bg-bg">
      {/* Header */}
      <header className="bg-bgCard border-b shadow-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center space-x-2">
            <ArrowLeft className="w-6 h-6" />
            <span className="font-medium">Back</span>
          </Link>

          <div className="flex flex-1 items-center justify-end space-x-2">
            {loginuserRole === "ADMIN" && (
              <div className="flex items-center space-x-4">
                <button
                  type="button"
                  onClick={handlePublishToggle}
                  className="bg-bg text-text px-4 py-2 rounded-full hover:border-primary hover:border-2"
                >
                  {loading ? (
                    <SpinnerIcon />
                  ) : eventData.publish ? (
                    "Click to Unpublish"
                  ) : (
                    "Click to Publish"
                  )}
                </button>

                <MainButton
                  onClick={deletePost}
                  className="rounded-full"
                  label={deleteLoading ? <SpinnerIcon /> : "Delete"}
                />
              </div>
            )}
            {loginuserRole === "USER" && (
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleFavorite}
                  className={`text-2xl ${
                    isFav ? "text-red-500" : "text-gray-500"
                  }`}
                >
                  {isFav ? <FaHeart /> : <FaRegHeart />}
                </button>
                <MainButton
                  onClick={handleJoin}
                  className="rounded-full"
                  label={isJoin ? "Not Interested" : "Interested"}
                />
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto p-4 space-y-8">
        {/* Event Banner */}
        <div className="relative aspect-video rounded-lg overflow-hidden">
          <Image
            src={eventData.image}
            alt={eventData.eventname}
            width={1024}
            height={700}
            className="object-cover w-full h-full"
          />
        </div>

        {/* Event Details */}
        <div className="space-y-4">
          <div className="flex justify-between">
            <h1 className="text-xl font-medium">{eventData.eventname}</h1>
            <p className="text-lg font-medium">
              {Number(eventData.fee) > 0 ? `$${eventData.fee}` : "Free"}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 ">
            <div className="bg-bgCard rounded-md px-3 py-2 flex gap-x-2 items-center">
              <div className="rounded-full bg-red-300 p-3">
                <svg
                  className=" h-6 w-6 text-primary"
                  fill="none"
                  height="24"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  width="24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect height="18" rx="2" ry="2" width="18" x="3" y="4" />
                  <line x1="16" x2="16" y1="2" y2="6" />
                  <line x1="8" x2="8" y1="2" y2="6" />
                  <line x1="3" x2="21" y1="10" y2="10" />
                </svg>
              </div>
              <div>
                {" "}
                <h2 className="text-base font-medium">Date & Time</h2>
                <p className="text-gray-400">
                  {moment(eventData.startdate).format("DD MMM, YYYY")} -{" "}
                  {moment(eventData.enddate).format("DD MMM, YYYY")}
                </p>
                <p className="text-gray-400">
                  {eventData.starttime} - {eventData.endtime}
                </p>
              </div>
            </div>
            <div className="bg-bgCard rounded-md px-3 py-2 flex gap-x-2 items-center">
              <div className="rounded-full bg-red-300 p-3">
                <svg
                  className=" h-6 w-6 text-primary"
                  fill="none"
                  height="24"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  width="24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </div>
              <div>
                <h2 className="text-base font-medium">Location</h2>
                <p className="text-gray-400">{eventData.addressdetail}</p>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 justify-between">
            {author && (
              <div className="flex items-center space-x-4">
                <img
                  src={author.avatar}
                  alt={author.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <p className="font-medium">{author.name}</p>
                  <p className="text-sm text-gray-500">Event Organizer</p>
                </div>
              </div>
            )}

            {loginuserRole === "USER" && (
              <MainButton
                onClick={handleFollow}
                className="rounded-full"
                label={isFollower ? "Unfollow" : "Follow"}
              />
            )}
          </div>
          <div>
            {eventData.organize && (
              <div className="space-y-2">
                <h2 className="text-lg font-medium">Event Organizer</h2>
                <p className="text-muted-foreground">
                  <span className="text-base font-medium mr-2">
                    Organizer :
                  </span>
                  <span className="text-sm">{eventData.organize}</span>
                </p>
                <p className="text-muted-foreground">
                  <span className="text-base font-medium mr-2">
                    Organizer Person :
                  </span>
                  <span className="text-sm">{eventData.organizePerson}</span>
                </p>
                <p className="text-muted-foreground">
                  <span className="text-base font-medium mr-2">
                    Organizer Contact Details :
                  </span>
                  <span className="text-sm">{eventData.contactPerson}</span>
                </p>
              </div>
            )}
          </div>
          <div>
            <h2 className="text-lg font-medium">About Event</h2>
            <p className="text-text text-sm opacity-50">
              {eventData.description}
            </p>
          </div>
        </div>

        {/* Participants */}

        {participants.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold">Participants</h2>
            <div className="flex items-center -space-x-2">
              {participants.map((participant, idx) => (
                <img
                  key={idx}
                  src={participant.avatar}
                  alt={participant.name}
                  className="w-10 h-10 rounded-full object-cover border border-white"
                />
              ))}
              <div>
                {participants.length > 3 && (
                  <span className="text-sm text-text mx-3">
                    +{participants.length - 3}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
        {eventData.youtubelink && (
          <div className="space-y-2 h-96">
            <h2 className="text-lg font-medium">Event Video</h2>
            <ReactPlayer
              url={eventData.youtubelink}
              controls
              width="100%"
              height="100%"
            />
          </div>
        )}

        {eventData.isVirtual ? (
          <div>
            <div className="h-3"></div>
            <div className="space-y-6 py-12 bg-background mt-2 rounded-lg shadow-lg p-6">
              <h2 className="text-lg font-medium">This event is Virtual</h2>
              <p className="">
                Join the event online by clicking the link below:
              </p>
              <div>
                <a
                  href={
                    eventData.meetingLink?.startsWith("http")
                      ? eventData.meetingLink
                      : `https://${eventData.meetingLink}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline hover:text-primary "
                >
                  Join Event Now
                </a>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-2 py-6">
            <h2 className="text-lg font-medium">Location</h2>
            <div className="aspect-[2/1] overflow-hidden rounded-lg">
              <LocationMap lat={eventData.latitude} lng={eventData.longitude} />
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default EventDetailComponent;
